import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const AdminNavBar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    {
      path: '/analytics-dashboard',
      label: 'Analytics',
      icon: 'BarChart3',
      description: 'View participation metrics'
    },
    {
      path: '/participant-registration',
      label: 'Registration',
      icon: 'Users',
      description: 'Manage participants'
    }
  ];

  const isActive = (path) => location?.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    onLogout();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <nav className="sticky top-0 z-100 bg-card border-b border-border shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Zap" size={20} color="var(--color-primary)" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-lg font-semibold text-foreground">XAIKUS Admin</h1>
                  <p className="text-xs text-muted-foreground">Digital Roulette Management</p>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-2 ml-8">
                {navigationItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg transition-smooth
                      ${isActive(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'text-foreground hover:bg-muted'
                      }
                    `}
                    title={item?.description}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span className="font-medium text-sm">{item?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/roulette-game-screen')}
                iconName="ArrowLeft"
                iconPosition="left"
                className="hidden sm:flex"
              >
                Back to Game
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                iconName="LogOut"
                iconPosition="left"
                className="hidden sm:flex"
              >
                Logout
              </Button>

              <button
                onClick={toggleMobileMenu}
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-smooth focus-ring"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-card animate-slide-in">
            <div className="px-4 py-4 space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth text-left
                    ${isActive(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name={item?.icon} size={20} />
                  <div className="flex-1">
                    <div className="font-medium">{item?.label}</div>
                    <div className={`text-xs ${isActive(item?.path) ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                      {item?.description}
                    </div>
                  </div>
                  {isActive(item?.path) && (
                    <Icon name="Check" size={16} />
                  )}
                </button>
              ))}

              <div className="pt-4 mt-4 border-t border-border space-y-2">
                <Button
                  variant="ghost"
                  fullWidth
                  onClick={() => {
                    navigate('/roulette-game-screen');
                    setIsMobileMenuOpen(false);
                  }}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Back to Game
                </Button>

                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleLogout}
                  iconName="LogOut"
                  iconPosition="left"
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
      <div className="bg-muted/30 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Home" size={14} color="var(--color-muted-foreground)" />
            <span className="text-muted-foreground">/</span>
            <span className="text-foreground font-medium">
              {navigationItems?.find(item => isActive(item?.path))?.label || 'Admin'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminNavBar;